import { fetchAuthCode, getTokenByAuthCode, getLoginFormUri } from './auth-lib';
import { AuthLibSettings } from './interfaces';
import { getPkcePair } from './pkce';
import { getUrlParam, navigateTo, validateSettings } from './util';
import { version } from '../../package.json'

export const LIB_NAME = 'oauth2-client';
export const PKCE_VERIFIER_KEY = `${LIB_NAME}_pkceVerifier`;
export const VERSION = version;

const storedTokens: { [key: string]: string } = {};
const timeoutHandles: { [key: string]: number } = {};

/**
 * Returns the value of the 'navsid' URL query parameter
 */
export function getNavsidFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  if (params.has('navsid')) {
    return params.get('navsid');
  }

  return null;
}

/**
 * Returns a promise that will be resolved with an access token
 * @param settings
 */
export async function getAccessToken(settings: AuthLibSettings): Promise<string> {
  validateSettings(settings);

  const cacheKey = `${settings.clientId}_${settings.scope}`;

  if (timeoutHandles[cacheKey]) {
    window.clearTimeout(timeoutHandles[cacheKey]);
  }

  if (storedTokens[cacheKey]) {
    return storedTokens[cacheKey];
  }

  const pkce = await getPkcePair();

  let authCode: string;
  if (typeof settings.authCode === 'string') {
    authCode = settings.authCode;
  } else {
    authCode = getUrlParam('code');
  }

  if (!authCode && settings.mode === 'standalone') {
    sessionStorage.setItem(PKCE_VERIFIER_KEY, pkce.verifier);
    navigateTo(getLoginFormUri(settings, pkce.challenge));
    return Promise.reject('Login required, redirecting...');
  }

  if (!authCode) {
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    try {
      const authCodeData = await fetchAuthCode(settings, pkce.challenge);
      authCode = authCodeData.code;
    } catch (err) {
      const msg = `${LIB_NAME} error while fetching the auth code`;
      throw new Error(`${msg}: ${err.message}`);
    }
  }

  let accessToken: string;
  let tokenExpires: number;
  try {
    let verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (verifier) {
      sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    } else {
      verifier = pkce.verifier;
    }
    const tokenPayload = await getTokenByAuthCode(authCode, settings, verifier);
    accessToken = tokenPayload.access_token;
    tokenExpires = tokenPayload.expires_in;
  } catch (err) {
    const msg = `${LIB_NAME} error while fetching an access token for auth code ${authCode}`;
    throw new Error(`${msg}: ${err.message}`);
  }

  try {
    storedTokens[cacheKey] = accessToken;

    // fetch a fresh token before the last one expires
    if (settings.navsid) {
      timeoutHandles[cacheKey] = window.setTimeout(async () => {
        storedTokens[cacheKey] = null;
        await getAccessToken(settings);
      }, tokenExpires * 1000 - 1000);
    }

    return storedTokens[cacheKey];
  } catch (err) {
    /* istanbul ignore next */
    const msg = `${LIB_NAME} unexpected error`;
    /* istanbul ignore next */
    throw new Error(`${msg}: ${err.message}`);
  }
}

// TODO: partial settings?
/**
 * Returns a promise that will be resolved with the URI of the login page. After logging in, the user will be
 * redirected to the configured redirectUri
 * @param settings
 */
export async function getLoginUri(settings: AuthLibSettings): Promise<string> {
  const pkce = await getPkcePair();
  sessionStorage.setItem(PKCE_VERIFIER_KEY, pkce.verifier);

  return getLoginFormUri(settings, pkce.challenge);
}

// TODO: partial settings?
export async function getAccessTokenFromCode(settings: AuthLibSettings): Promise<string> {
  let authCode: string;
  if (typeof settings.authCode === 'string') {
    authCode = settings.authCode;
  } else {
    authCode = getUrlParam('code');
  }

  if (!authCode) {
    return Promise.reject('No authCode found in settings or URL!');
  }

  return await getAccessToken(settings);
}
